import { useState } from "react";
import { FileText, BarChart3, MessageSquareText, Pencil, Trash2, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { ErrorState } from "@/components/ui/error-state";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { usePromptsStore } from "@/stores/prompts";
import type { Prompt } from "../../../types";

interface PromptsTableProps {
  onEdit?: (prompt: Prompt) => void;
  onDeleteComplete?: (mode: "deleted" | "archived") => void;
}

export function PromptsTable({ onEdit, onDeleteComplete }: PromptsTableProps) {
  const { prompts, loading, error } = usePromptsStore();
  const fetch = usePromptsStore((s) => s.fetch);
  const remove = usePromptsStore((s) => s.remove);
  const [deletingPromptId, setDeletingPromptId] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState("");

  /**
   * Deletes or archives a prompt after user confirmation.
   *
   * @param prompt Prompt selected for deletion.
   * @return Promise that resolves after deletion finishes.
   */
  const handleDelete = async (prompt: Prompt): Promise<void> => {
    const confirmed = confirm(
      `Delete "${prompt.name}"? If it has historical results, Prism will archive it and hide it from new runs.`,
    );

    if (!confirmed) return;

    setDeletingPromptId(prompt.id);
    setDeleteError("");

    try {
      const mode = await remove(prompt.id);
      onDeleteComplete?.(mode);
    } catch (err: any) {
      setDeleteError(err?.message || "Failed to delete prompt");
    } finally {
      setDeletingPromptId(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <div className="p-6 space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 bg-slate-100 rounded-md animate-subtle-pulse" />
          ))}
        </div>
      </Card>
    );
  }

  if (error && !loading) {
    return <ErrorState message={error} onRetry={fetch} />;
  }

  if (prompts.length === 0) {
    return (
      <EmptyState
        icon={FileText}
        title="No prompts yet"
        description="Create a scoring template to define how brands should be evaluated."
      />
    );
  }

  return (
    <Card className="overflow-hidden">
      {deleteError && (
        <div className="border-b border-red-100 bg-red-50 px-4 py-3" role="alert">
          <p className="text-[13px] text-red-700">{deleteError}</p>
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Topic</TableHead>
            <TableHead>Dimensions</TableHead>
            <TableHead className="w-24" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {prompts.map((prompt) => (
            <TableRow key={prompt.id}>
              <TableCell className="font-medium text-brand-950">
                {prompt.name}
              </TableCell>
              <TableCell>
                <Badge variant={prompt.type === "sentiment" ? "default" : "muted"}>
                  <span className="flex items-center gap-1">
                    {prompt.type === "sentiment" ? (
                      <MessageSquareText className="w-3 h-3" />
                    ) : (
                      <BarChart3 className="w-3 h-3" />
                    )}
                    {prompt.type || "scoring"}
                  </span>
                </Badge>
              </TableCell>
              <TableCell>
                <Badge variant="category">
                  {prompt.category || "general"}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {prompt.dimensions.map((d) => (
                    <span
                      key={d}
                      className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500"
                    >
                      {d}
                    </span>
                  ))}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-slate-400 hover:text-brand-600"
                    onClick={() => onEdit?.(prompt)}
                    aria-label={`Edit ${prompt.name}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-slate-400 hover:text-red-600"
                    onClick={() => handleDelete(prompt)}
                    disabled={deletingPromptId === prompt.id}
                    aria-label={`Delete ${prompt.name}`}
                  >
                    {deletingPromptId === prompt.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
